import { defineStore } from "pinia";
import socket from "@/services/socket";
import { usePlayersStore } from "./usePlayersStore";
import { useSessionStore } from "./useSessionStore";

import type { Persist, RochamboMatch, RochamboPick } from "@/types";

const BEATS: Record<RochamboPick, RochamboPick> = {
  rock: "scissors",
  paper: "rock",
  scissors: "paper",
};

export const ROCHAMBO_PICKS: RochamboPick[] = ["rock", "paper", "scissors"];

interface RochamboState {
  match: RochamboMatch | null;
  ownPick: RochamboPick | null;
  minimized: boolean;
}

export const useRochamboStore = defineStore("rochambo", {
  state: (): RochamboState => ({
    match: null,
    ownPick: null,
    minimized: false,
  }),

  getters: {
    sharedMatch: (state): RochamboMatch | null => {
      const match = state.match;
      if (!match) return null;
      return {
        id: match.id,
        players: [match.players[0], match.players[1]],
        submitted: [match.submitted[0], match.submitted[1]],
        picks: match.revealed ? [match.picks[0], match.picks[1]] : [null, null],
        revealed: match.revealed,
      };
    },

    winner: (state): number | null => {
      const match = state.match;
      if (!match?.revealed) return null;
      const [first, second] = match.picks;
      if (!first || !second) return null;
      if (first === second) return -1;
      return BEATS[first] === second ? match.players[0] : match.players[1];
    },
  },

  actions: {
    start(players: [number, number]) {
      const session = useSessionStore();
      if (session.isPlayerOrSpectator) return;
      if (players[0] === players[1]) return;
      this.match = {
        id: Date.now(),
        players,
        submitted: [false, false],
        picks: [null, null],
        revealed: false,
      };
      this.ownPick = null;
      this.minimized = false;
      this.sync();
    },

    sync() {
      const session = useSessionStore();
      if (session.isPlayerOrSpectator) return;
      socket.send("rochambo", this.sharedMatch);
    },

    setMatch(match: RochamboMatch | null) {
      const isSameMatch = !!match && this.match?.id === match.id;
      const justRevealed = !!match?.revealed && !this.match?.revealed;
      this.match = match;
      if (!isSameMatch) {
        this.ownPick = null;
      }
      if (!isSameMatch || justRevealed) {
        this.minimized = false;
      }
    },

    pick(seat: number, pick: RochamboPick) {
      const session = useSessionStore();
      const match = this.match;
      if (!match || match.revealed) return;
      const slot = match.players.indexOf(seat);
      if (slot === -1) return;

      if (!session.isPlayerOrSpectator) {
        this.receivePick([seat, pick]);
        return;
      }

      const player = usePlayersStore().players[seat];
      if (!player || player.id !== session.playerId) return;
      this.ownPick = pick;
      match.submitted[slot] = true;
      socket.sendDirect("host", "rochamboPick", [seat, pick, session.playerId]);
    },

    receivePick([seat, pick]: [number, RochamboPick]) {
      const session = useSessionStore();
      if (session.isPlayerOrSpectator) return;
      const match = this.match;
      if (!match || match.revealed) return;
      const slot = match.players.indexOf(seat);
      if (slot === -1) return;
      match.picks[slot] = pick;
      match.submitted[slot] = true;
      this.sync();
    },

    reveal() {
      const session = useSessionStore();
      if (session.isPlayerOrSpectator || !this.match) return;
      if (this.match.picks.some((pick) => !pick)) return;
      this.match.revealed = true;
      this.minimized = false;
      this.sync();
    },

    end() {
      const session = useSessionStore();
      this.match = null;
      this.ownPick = null;
      this.minimized = false;
      if (!session.isPlayerOrSpectator) socket.send("rochambo", null);
    },

    remapSeats(remap: (seat: number) => number) {
      const match = this.match;
      if (!match) return;
      const players: [number, number] = [
        remap(match.players[0]),
        remap(match.players[1]),
      ];
      if (players[0] < 0 || players[1] < 0) {
        this.end();
        return;
      }
      if (players[0] === match.players[0] && players[1] === match.players[1])
        return;
      match.players = players;
      this.sync();
    },

    toggle() {
      this.minimized = !this.minimized;
    },
  },

  persist: {
    pick: ["match", "ownPick"],
  } as Persist<RochamboState>,
});
